<script lang="ts" setup>
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { API } from "@/api/api";
import {
  type CheckCallDTO,
  type CheckCallInfoDTO,
  type CheckCallRouteStopDTO,
  CheckCallType,
  type CreateCheckCallRequest,
  DeviationStatus,
} from "@/api/trips/dto/check-call";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import {
  CheckCallMarker,
  EndPointMarker,
  PathArrowReverse,
  StartDispatchPointMarker,
  StartPointMarker,
} from "@/constants/markers";
import { OrderStopType } from "@/constants/order";
import { decode } from "@googlemaps/polyline-codec";
// @ts-ignore
import LocationPicker from "@/components/pickers/LocationPicker.vue";
// @ts-ignore
import DateTimePicker from "@/components/pickers/DateTimePicker.vue";
import { FilterUtils } from "@/services/filters";
import { useAppStore } from "@/store/app.store";
import { DialogSize } from "@/constants/dialog";
import DeviationStatusLabel from "@/views/trips/components/DeviationStatusLabel.vue";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin";
import { Marker as GMarker, Polyline as GPolyline } from "vue3-google-map";
import OmniGoogleMap from "@/components/omni/OmniGoogleMap.vue";
import { useBaseView, useDateTime, useFilters } from "@/composables";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import dayjs from "dayjs";
import type { TripMonitorRouteStop } from "@/api/trips/dto/trip-monitor";
import TruckLocationInfo from "@/components/truck/TruckLocationInfo.vue";
import type { LocationTruckDto } from "@/api/trucks/dto/truck.dto";
import { TWENTY_MINUTES_IN_SECONDS } from "@/constants/time";
import type { AddressDto } from "@/api/geo/dto/address.dto";
import type { VForm } from "vuetify/components";
import { useI18n } from "vue-i18n";
import TruckLink from "@/components/links/TruckLink.vue";
import CheckCallMapLegendContent from "@/views/trips/components/CheckCallMapLegendContent.vue";

const api = new API();
const mapUtils = GoogleMapsMixin.methods;
const filters = new FilterUtils();
const orderUtils = OrdersMixin.methods!;

const appStore = useAppStore();
const { scrollToError } = useBaseView();
const {
  formatTimeDiff,
  dateToUTC,
  dateTZ,
  hasTimePassed,
  systemDateTimeFormatWithTZ,
  systemDateTimeFormat,
  getLocalTimeLabel,
} = useDateTime();
const { t } = useI18n();
const { miles } = useFilters();

const showDialog = defineModel<boolean>({ default: false });
const preview = defineModel<boolean>("preview", { default: false });
const props = defineProps<{
  modelValue: boolean;
  routeId: number;
  routeStops: RouteStopDto[] | TripMonitorRouteStop[];
  creationLoading: boolean;
  checkCallPreview: CheckCallDTO | null;
  title: string;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:preview", value: boolean): void;
  (e: "success", checkCall: CheckCallDTO): void;
  (e: "preview", checkCall: CreateCheckCallRequest): void;
  (e: "create", checkCall: CreateCheckCallRequest): void;
}>();

const checkCallInfo = ref<CheckCallInfoDTO>();
const truckLocation = ref<LocationTruckDto | null>(null);
const initLoading = ref<boolean>(false);
const location = ref<AddressDto | null>(null);
const date = ref<string>("");
const mapCenter = ref();
const mapZoom = ref();
const showEmptyMilesDialog = ref<boolean>(false);

const formRef = useTemplateRef<null | VForm>("form");

const primaryActionLabel = computed<string>(() => (preview.value ? "Create" : "Preview"));
const lineSymbol = {
  path: "M0-1V1Z",
  strokeColor: "#4CAF50",
  scale: 6,
  strokeOpacity: 1,
};
const routeLineOptions = computed(() => {
  const path = checkCallInfo.value?.routePolyline
    ? decode(checkCallInfo.value!.routePolyline!).map((tuple) => ({
        lat: tuple[0],
        lng: tuple[1],
      }))
    : null;
  return {
    strokeOpacity: 0,
    icons: [
      {
        icon: lineSymbol,
        offset: "0",
        repeat: "24px",
      },
      {
        icon: PathArrowReverse,
        repeat: "24px",
        offset: "0",
      },
    ],
    path,
  };
});

const checkCallsLineOptions = computed(() => {
  const path = checkCallInfo.value?.checkCallsPolyline
    ? decode(checkCallInfo.value!.checkCallsPolyline!).map((tuple) => ({
        lat: tuple[0],
        lng: tuple[1],
      }))
    : null;
  return {
    icons: [
      {
        icon: PathArrowReverse,
        offset: "0",
        repeat: "50px",
      },
    ],
    strokeWeight: 6,
    strokeColor: "#304FFE",
    path,
  };
});

const checkCallMarkers = computed(() => {
  return checkCallInfo.value?.checkCalls
    .filter((cc) => cc.type === CheckCallType.checkCall)
    .map((cc) => ({
      id: cc.id,
      options: {
        position: {
          lat: cc.locationCoordinates.lat,
          lng: cc.locationCoordinates.lon,
        },
        icon: CheckCallMarker,
      },
    }));
});

const startDispatchMarker = computed(() => {
  const startDispatch = checkCallInfo.value?.checkCalls.find((cc) => cc.type === CheckCallType.dispatchConfirmed);
  return (
    startDispatch && {
      position: {
        lat: startDispatch.locationCoordinates.lat,
        lng: startDispatch.locationCoordinates.lon,
      },
      icon: StartDispatchPointMarker,
      label: {
        text: "Start",
        className: "map__label start-dispatch",
      },
    }
  );
});

const pickerTimeLabel = computed(() => {
  if (date.value) {
    return getLocalTimeLabel(date.value, location.value?.timezone);
  }

  return "";
});

const nextStop = computed(() => {
  if (!props.routeStops || !props.checkCallPreview) {
    return null;
  }

  return props.checkCallPreview.nextStopId
    ? props.routeStops.find((rs) => rs.id === props.checkCallPreview?.nextStopId)
    : null;
});

const isCheckCallInFuture = computed(() => {
  if (!location.value?.timezone || !date.value) {
    return false;
  }

  const comparedDate = dateToUTC(date.value, location.value.timezone);

  return comparedDate.isAfter(dayjs().utc().add(1, "minute"));
});

const checkCallAlertMessage = computed(() => {
  const dateInUTC = dateToUTC(date.value, location.value?.timezone || "");
  return `Pay attention, selected time for check call is in future(${formatTimeDiff(dateInUTC)})`;
});

const isETALaterThanNextStop = computed(() => {
  return dayjs(props.checkCallPreview?.eta).isAfter(dayjs(nextStop.value?.timeTo));
});

const classETA = computed(() => {
  return isETALaterThanNextStop.value ? "text-danger border-danger" : "text-success border-success";
});

const shouldPrefillLocation = computed(() => {
  if (!checkCallInfo.value?.truck?.currentLocationTime) {
    return false;
  }

  return !hasTimePassed(checkCallInfo.value.truck.currentLocationTime, TWENTY_MINUTES_IN_SECONDS);
});

const loading = computed(() => {
  return initLoading.value || props.creationLoading;
});

const emptyMiles = computed(() => {
  return Math.ceil(Number(miles(props.checkCallPreview?.nextStopDistance)));
});

const isDispatchConfirmedType = computed(() => {
  return props.checkCallPreview?.type === CheckCallType.dispatchConfirmed;
});

const isEmptyMilesLimitExceededAlert = computed(() => {
  if (!props.checkCallPreview?.nextStopDistance || !isDispatchConfirmedType.value) {
    return false;
  }

  const emptyMilesLimit = 150;

  return emptyMiles.value > emptyMilesLimit;
});

const isExceeded300Miles = computed(() => {
  return emptyMiles.value > 300;
});

const emptyMilesAlertBG = computed(() => {
  return isExceeded300Miles.value ? "red-lighten-5" : "amber-lighten-5";
});

const emptyMilesAlertIconColor = computed(() => {
  return isExceeded300Miles.value ? "danger" : "warning";
});

const milesAlertExceeded = computed(() => {
  return isExceeded300Miles.value ? 300 : 150;
});

async function init() {
  initLoading.value = true;
  date.value = "";
  location.value = null;
  const response = await api.events.previewCheckCall(props.routeId);

  if (!response.success || !response.data) {
    return;
  }

  checkCallInfo.value = response.data;

  truckLocation.value = {
    coordinates: checkCallInfo.value.truck.currentLocation,
    timezone: checkCallInfo.value.truck.currentLocationTimezone,
    city: checkCallInfo.value.truck.currentAddress,
    fullAddress: checkCallInfo.value.truck.currentAddress,
  };

  setMapCenter(checkCallInfo.value);
  if (shouldPrefillLocation.value) {
    pointCurrentLocation();
  }

  initLoading.value = false;
}

watch(
  showDialog,
  async (value) => {
    if (value) {
      await init();
    } else {
      emits("update:preview", false);
    }
  },
  {
    immediate: true,
  },
);

watch(location, async (value) => {
  if (value) {
    await nextTick();

    date.value = dateTZ(dayjs(), value?.timezone || "", systemDateTimeFormat);
  }
});

async function onPrimaryAction(isConfirmed = false) {
  const result = await formRef.value?.validate();

  if (!result?.valid || !location.value?.fullAddress || !location.value?.timezone) {
    scrollToError();

    return;
  }

  const request: CreateCheckCallRequest = {
    checkCallTime: dateToUTC(date.value, location.value.timezone).format(systemDateTimeFormatWithTZ),
    locationAddress: location.value.fullAddress,
    locationCoordinates: {
      lat: location.value.lat,
      lon: location.value.lon,
    },
    commit: false,
  };

  if (!preview.value) {
    emits("update:preview", true);
    emits("create", request);

    return;
  }

  if (isExceeded300Miles.value && isDispatchConfirmedType.value && !isConfirmed) {
    showEmptyMilesDialog.value = true;

    return;
  }

  request.commit = true;
  showEmptyMilesDialog.value = false;
  emits("create", request);
  emits("update:modelValue", false);
  emits("update:preview", false);
}

function getFacilityMarker(stop: CheckCallRouteStopDTO) {
  return {
    position: {
      lat: stop.facilityLocation.lat,
      lng: stop.facilityLocation.lon,
    },
    label:
      stop.type === OrderStopType.pickup
        ? {
            text: "Pick up",
            className: "map__label start",
          }
        : {
            text: "Delivery",
            className: "map__label end",
          },
    icon: stop.type === OrderStopType.pickup ? StartPointMarker : EndPointMarker,
  };
}

function pointCurrentLocation() {
  if (!checkCallInfo.value?.truck.currentLocation) {
    return;
  }

  location.value = {
    fullAddress: checkCallInfo.value?.truck.currentAddress,
    lat: checkCallInfo.value?.truck.currentLocation?.lat,
    lng: checkCallInfo.value?.truck.currentLocation?.lon,
    lon: checkCallInfo.value?.truck.currentLocation?.lon,
    timezone: checkCallInfo.value?.truck.currentLocationTimezone,
  };
}

function getDeviationStatusClass(deviationStatus: DeviationStatus): string {
  switch (deviationStatus) {
    case DeviationStatus.onTrack:
      return "text-success";
    case DeviationStatus.deviated:
      return "text-warning";
    case DeviationStatus.wrongWay:
      return "text-error";
    default:
      return "";
  }
}

function setMapCenter(checkCallInfo: CheckCallInfoDTO) {
  const coordinates = [...decode(checkCallInfo.routePolyline || ""), ...decode(checkCallInfo.checkCallsPolyline || "")];
  const { minLat, maxLat, minLon, maxLon } = coordinates.reduce(
    (acc, [lat, lon]) => ({
      minLat: Math.min(acc.minLat, lat),
      maxLat: Math.max(acc.maxLat, lat),
      minLon: Math.min(acc.minLon, lon),
      maxLon: Math.max(acc.maxLon, lon),
    }),
    {
      minLat: Infinity,
      maxLat: -Infinity,
      minLon: Infinity,
      maxLon: -Infinity,
    },
  );

  mapCenter.value = { lat: (minLat + maxLat) / 2, lng: (minLon + maxLon) / 2 };
  mapZoom.value = 6;
}
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :disabled="loading"
    :loading="loading"
    :primary-action-label="primaryActionLabel"
    :size="DialogSize.xlarge"
    :title="title"
    @primary-action:click="onPrimaryAction"
  >
    <v-form ref="form" lazy-validation>
      <slot name="subtitle"></slot>
      <transition mode="out-in">
        <div v-if="!preview">
          <div class="d-flex align-center mb-6">
            <div class="bg-grey-lighten-4 rounded pa-3 w-100 mr-3">
              <div class="text-grey-darken-1 font-size-12 mb-1">Last known location</div>
              <div v-if="truckLocation?.coordinates && checkCallInfo?.truck" class="d-flex align-center mb-1">
                <truck-location-info
                  :location="truckLocation"
                  :time="checkCallInfo.truck.currentLocationTime"
                  class="w-100"
                  show-alert
                  show-indicator
                />
              </div>
            </div>
            <v-btn
              :disabled="!truckLocation?.coordinates"
              color="primary"
              density="compact"
              icon="mdi-arrow-down"
              variant="text"
              @click="pointCurrentLocation"
            ></v-btn>
          </div>
          <location-picker v-model="location" class="mb-6" required></location-picker>
          <date-time-picker
            v-model="date"
            :time-label="pickerTimeLabel"
            class="mb-6"
            hide-details
            required
          ></date-time-picker>
        </div>
        <div v-else-if="checkCallPreview" class="bg-grey-lighten-4 rounded mb-6 pa-3">
          <div class="text-grey-darken-1 font-size-12 mb-4">Current check call</div>
          <div class="d-flex justify-space-between">
            <div>
              <div class="text-grey-darken-2 mb-2">Location:</div>
              <div class="mb-6 d-flex align-center">
                <span class="text-grey-darken-3 font-weight-500" data-qa="check-call-modal-location">
                  {{ checkCallPreview.locationAddress }}
                </span>
                <v-icon class="text-green ml-2" icon="mdi-map-marker-radius-outline" size="18"></v-icon>
              </div>
              <div class="text-grey-darken-2 mb-2">Check call time:</div>
              <div class="mb-6">
                <div class="text-grey-darken-3 font-weight-500" data-qa="check-call-modal-time-1">
                  {{ dateTZ(checkCallPreview.checkCallTime, appStore.dispatcher!.timezone) }}
                </div>
                <div class="text-grey-darken-1" data-qa="check-call-modal-time-2">
                  {{ dateTZ(checkCallPreview.checkCallTime, checkCallPreview.timezone) }}
                </div>
              </div>
              <div class="text-grey-darken-2 mb-2">Miles left to:</div>
              <div class="text-grey-darken-3 font-weight-500">
                <template v-if="checkCallPreview.nextStopDistance">
                  <span class="text-grey-darken-3 font-weight-500" data-qa="check-call-modal-miles-left-total">{{
                    filters.miles(checkCallPreview.nextStopDistance, 0)
                  }}</span>
                  <span v-if="checkCallPreview?.distanceReducing !== null" class="ml-1">miles</span>
                  <span
                    v-if="checkCallPreview?.distanceReducing !== null"
                    :class="{
                      'text-success': checkCallPreview.distanceReducing! < 0,
                      'text-error': checkCallPreview.distanceReducing! >= 0,
                    }"
                    class="ml-1 font-weight-500"
                    data-qa="check-call-modal-miles-left-extra"
                  >
                    {{ filters.miles(checkCallPreview.distanceReducing, 0) }} miles
                  </span>

                  <span v-else class="ml-1 font-weight-500" data-qa="check-call-modal-miles-left-extra">
                    {{ filters.miles(checkCallPreview.distanceReducing, 0) }} miles
                  </span>
                </template>
                <template v-else> -</template>
              </div>
            </div>
            <div>
              <div class="text-grey-darken-2 mb-2">From last check call:</div>
              <div class="text-grey-darken-3 font-weight-500 mb-6">
                <template v-if="checkCallPreview.previousCallDistance">
                  <span class="text-grey-darken-3 font-weight-500" data-qa="check-call-modal-from-last-cc-total">
                    {{ filters.miles(checkCallPreview.previousCallDistance, 0) }} miles
                  </span>
                  <span
                    :class="getDeviationStatusClass(checkCallPreview.currentDeviationStatus!)"
                    class="font-weight-500"
                    data-qa="check-call-modal-from-last-cc-extra"
                  >
                    ({{ filters.miles(checkCallPreview.currentDeviation, 0) }})
                  </span>
                  <deviation-status-label
                    :deviation-status="checkCallPreview.currentDeviationStatus!"
                    class="ml-3"
                    data-qa="check-call-modal-from-last-cc-status"
                  ></deviation-status-label>
                </template>
                <template v-else> -</template>
              </div>
              <template v-if="nextStop">
                <div class="text-grey-darken-2 mb-2">Next stop time:</div>
                <div class="mb-6">
                  <div class="d-flex text-grey-darken-3 font-weight-500">
                    <div class="mr-1" data-qa="next-stop-time-type">
                      {{ orderUtils.getTimeType(nextStop.timeType) }}:
                    </div>
                    <div>
                      <div data-qa="next-stop-time-1">
                        {{
                          orderUtils.getTimeInterval(nextStop.timeFrom, nextStop.timeTo, appStore.dispatcher!.timezone)
                        }}
                      </div>
                      <div data-qa="next-stop-time-2">
                        {{ orderUtils.getTimeInterval(nextStop.timeFrom, nextStop.timeTo, nextStop.timezone) }}
                      </div>
                    </div>
                  </div>
                  <div v-if="nextStop?.time2From" class="d-flex text-grey-darken-3 font-weight-500">
                    <div class="mr-1">{{ orderUtils.getTimeType(nextStop!.time2Type!) }}:</div>
                    <div>
                      <div>
                        {{
                          orderUtils.getTimeInterval(
                            nextStop.time2From,
                            nextStop.time2To,
                            appStore.dispatcher!.timezone,
                          )
                        }}
                      </div>
                      <div>
                        {{ orderUtils.getTimeInterval(nextStop.time2From, nextStop.time2To, nextStop.timezone) }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <div class="text-grey-darken-2 mb-2">From last stop:</div>
              <div class="text-grey-darken-3 font-weight-500">
                <template v-if="checkCallPreview.previousStopDistance">
                  <span class="text-grey-darken-3 font-weight-500" data-qa="check-call-modal-from-last-stop-total">
                    {{ filters.miles(checkCallPreview.previousStopDistance, 0) }} miles
                  </span>
                  <span
                    :class="getDeviationStatusClass(checkCallPreview.accumulatedDeviationStatus!)"
                    class="font-weight-500"
                    data-qa="check-call-modal-from-last-stop-extra"
                  >
                    ({{ filters.miles(checkCallPreview.accumulatedDeviation, 0) }})
                  </span>
                  <deviation-status-label
                    :deviation-status="checkCallPreview.accumulatedDeviationStatus!"
                    class="ml-3"
                    data-qa="check-call-modal-from-last-stop-status"
                  ></deviation-status-label>
                </template>
                <template v-else> -</template>
              </div>
            </div>
            <div>
              <div class="text-grey-darken-2 mb-2">Avg speed:</div>
              <div class="text-grey-darken-3 font-weight-500 mb-6" data-qa="check-call-modal-avg-speed">
                {{ filters.miles(checkCallPreview.avgSpeed, 0) }}
                miles/hour
              </div>
              <div class="text-grey-darken-2 mb-2">ETA:</div>
              <div :class="classETA" class="rounded py-1 px-2 font-weight-500">
                <div data-qa="check-call-modal-eta-time-1">
                  {{ dateTZ(checkCallPreview.eta!, appStore.dispatcher!.timezone) }}
                </div>
                <div data-qa="check-call-modal-eta-time-2">
                  {{ dateTZ(checkCallPreview.eta!, checkCallPreview.timezone) }}
                </div>
              </div>
            </div>
          </div>

          <alert-message
            v-if="isEmptyMilesLimitExceededAlert"
            :bg-color="emptyMilesAlertBG"
            :icon-color="emptyMilesAlertIconColor"
            class="mt-4"
            >{{ t("trip.distance-from-truck-to-pickup-exceeds") }} {{ milesAlertExceeded }}
            <span class="text-lowercase">{{ t("trip.empty-miles") }}</span></alert-message
          >
        </div>
      </transition>
    </v-form>

    <alert-message
      v-if="isCheckCallInFuture"
      bg-color="indigo-lighten-5"
      border-color="none"
      class="mt-1"
      color="indigo"
      icon-color="indigo"
    >
      {{ checkCallAlertMessage }}
    </alert-message>

    <omni-google-map
      :api-key="mapUtils.getApiKey()"
      :center="mapCenter"
      :fullscreen-control="false"
      :street-view-control="false"
      :zoom="4"
      class="mt-6"
      style="width: 100%; height: 500px"
    >
      <g-polyline :options="routeLineOptions"></g-polyline>
      <g-polyline :options="checkCallsLineOptions"></g-polyline>
      <g-marker v-for="stop in checkCallInfo?.routeStops" :key="stop.id" :options="getFacilityMarker(stop)"></g-marker>
      <g-marker
        v-for="checkCallMarker in checkCallMarkers || []"
        :key="checkCallMarker.id"
        :options="checkCallMarker.options"
      ></g-marker>
      <g-marker v-if="startDispatchMarker" :options="startDispatchMarker"></g-marker>
      <g-marker v-if="location" :options="{ position: { lat: location.lat, lng: location.lon } }"></g-marker>

      <template #mapLegend>
        <check-call-map-legend-content />
      </template>
    </omni-google-map>

    <omni-dialog
      v-model="showEmptyMilesDialog"
      :primary-action-label="t('general.confirm')"
      title="Empty Miles Exceeded"
      @primary-action:click="onPrimaryAction(true)"
    >
      <p class="text-grey-darken-1">
        {{ t("general.the-distance-from-the") }}
        <truck-link
          v-if="checkCallInfo?.truck"
          :truck-id="checkCallInfo.truck.id"
          :truck-number="checkCallInfo.truck.number"
        />
        {{ t("trip.pick-up-exceeds-300-miles") }}
      </p>
    </omni-dialog>
    <template #actions:custom>
      <v-btn
        v-if="preview"
        :disabled="loading"
        class="text-uppercase font-weight-600 font-size-14 px-4"
        color="primary"
        variant="outlined"
        @click="emits('update:preview', false)"
      >
        Edit
      </v-btn>
    </template>
  </omni-dialog>
</template>

<style lang="scss" scoped></style>
